import { createFileRoute, Link } from "@tanstack/react-router";
import {
	CheckCircle2,
	Eye,
	MessageSquare,
	ShieldCheck,
	UserCheck,
	Users,
} from "lucide-react";
import { FeatureGrid } from "@/components/feature-grid";
import { MarketingHero } from "@/components/marketing-hero";
import { MarketingPageShell } from "@/components/marketing-page-shell";
import { MarketingSection } from "@/components/marketing-section";
import { buildPageHead } from "@/lib/seo-meta";

const path = "/features/team-approval";

export const Route = createFileRoute(path)({
	component: TeamApprovalFeature,
	head: ({ matches }) =>
		buildPageHead({
			matches,
			internalPath: path,
			title: "AgentReacher | Team Approval",
			description:
				"Review and approve every post before it ships. AgentReacher's approval flow is built into every plan, with reviewer roles for teams and clients.",
		}),
});

function TeamApprovalFeature() {
	return (
		<MarketingPageShell>
			<MarketingHero
				eyebrow="Feature: team approvals"
				title="Approvals on every post. On every plan."
				description="Drafts go through the people who need to sign off before publishing. No more 'who tweeted that' moments."
			/>

			<MarketingSection
				title="What approval looks like"
				description="Reviewer roles built into the workflow, not bolted on."
			>
				<FeatureGrid
					features={[
						{
							icon: ShieldCheck,
							title: "Per-workspace approval gates",
							description:
								"Every workspace can require sign-off before posts publish. Configure per brand or client.",
						},
						{
							icon: Users,
							title: "Reviewer roles",
							description:
								"Strategists draft. Reviewers approve. Owners override. Roles map to the team you have.",
						},
						{
							icon: Eye,
							title: "Inline review",
							description:
								"Approve from the calendar or the post detail. No separate review tool to learn.",
						},
						{
							icon: MessageSquare,
							title: "Comment threads",
							description:
								"Leave feedback on a draft, request changes, and track edits in one place.",
						},
						{
							icon: UserCheck,
							title: "Client-friendly",
							description:
								"Invite a client as a reviewer. They approve their own brand without seeing others.",
						},
						{
							icon: CheckCircle2,
							title: "Audit trail",
							description:
								"See who drafted, edited, and approved every post. Useful for compliance.",
						},
					]}
				/>
			</MarketingSection>

			<MarketingSection>
				<div className="mx-auto max-w-2xl space-y-4 text-center text-sm leading-relaxed text-muted-foreground">
					<p>
						Approvals are part of every plan. Related:{" "}
						<Link
							to="/features/calendar"
							className="text-primary transition-colors hover:text-primary/80"
						>
							unified calendar
						</Link>
						,{" "}
						<Link
							to="/docs/roles"
							className="text-primary transition-colors hover:text-primary/80"
						>
							roles documentation
						</Link>
						,{" "}
						<Link
							to="/for/agencies"
							className="text-primary transition-colors hover:text-primary/80"
						>
							for agencies
						</Link>
						.
					</p>
				</div>
			</MarketingSection>
		</MarketingPageShell>
	);
}
